import { TableSortLabel, TableCell, darken } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import classnames from 'classnames';
import { lighten } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';

// How far one arrow-key press moves a column edge.
const KEYBOARD_STEP = 16;

const styles = theme => {
  return {
    head: {
      backgroundColor:
        theme.palette.mode === 'light'
          ? darken(theme.palette.background.paper, 0.1)
          : lighten(theme.palette.background.paper, 0.05),
      borderBottom: `1px solid ${theme.palette.divider}`,
      //   theme.palette.mode === 'light'
      //     ? lighten(alpha(theme.palette.divider, 1), 0.88)
      //     : darken(alpha(theme.palette.divider, 1), 0.68)
      // }`,
      flex: 1,
      // Anchors the resize handle to this column's trailing edge.
      position: 'relative',
    },
    column: {
      display: 'flex',
      alignItems: 'center',
      boxSizing: 'border-box',
      height: 48,
    },
    // Only for a resizable column. Now that the header shrinks to whatever width
    // it is given, a narrow one has to read as cut short rather than just cut.
    truncate: {
      overflow: 'hidden',
      '& span': {
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
    },
    resizeHandle: {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      width: 6,
      display: 'flex',
      justifyContent: 'center',
      cursor: 'col-resize',
      outline: 'none',
      zIndex: 1,
      // Visible at rest, so the column edges read as something you can take hold
      // of without having to go looking for them.
      '&::after': {
        content: '""',
        alignSelf: 'center',
        width: 2,
        height: '55%',
        borderRadius: 1,
        backgroundColor: theme.palette.divider,
        transition: theme.transitions.create(['background-color', 'height'], {
          duration: theme.transitions.duration.shortest,
        }),
      },
      '&:hover::after, &:focus-visible::after': {
        backgroundColor: theme.palette.text.secondary,
        height: '70%',
      },
    },
    // Kept lit for the whole drag; by then the pointer has left the handle.
    resizing: {
      '&::after': {
        backgroundColor: theme.palette.primary.main,
      },
    },
  };
};

const ArchTableHeaderCell = ({
  classes,
  children,
  style,
  dataKey,
  sortBy,
  sortDirection,
  disableSort,
  columnData,
}) => {
  const resize = columnData && columnData.resize;
  const [resizing, setResizing] = React.useState(false);

  const clamp = px =>
    Math.max(resize.minWidth, Math.min(resize.maxWidth, Math.round(px)));

  // The sort handler lives on react-virtualized's own wrapper around this cell,
  // so every event the handle takes has to stop there or a resize also re-sorts.
  const startResize = event => {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startWidth = resize.width;
    let latest = startWidth;
    let frame = null;

    const move = e => {
      latest = clamp(startWidth + e.clientX - startX);
      // One update a frame: the whole table re-renders behind this.
      if (frame === null) {
        frame = requestAnimationFrame(() => {
          frame = null;
          resize.onResize(latest, { persist: false });
        });
      }
    };

    const end = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', end);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      if (frame !== null) cancelAnimationFrame(frame);
      setResizing(false);
      resize.onResize(latest);
    };

    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', end);
    setResizing(true);
  };

  const handleKeyDown = event => {
    const step =
      { ArrowLeft: -KEYBOARD_STEP, ArrowRight: KEYBOARD_STEP }[event.key] || 0;
    if (!step) return;
    event.preventDefault();
    event.stopPropagation();
    resize.onResize(clamp(resize.width + step));
  };

  return (
    <TableCell
      component="div"
      variant="head"
      style={style}
      align={columnData && columnData.rightAlign ? 'right' : 'left'}
      className={classnames(classes.head, classes.column, {
        [classes.truncate]: Boolean(resize),
      })}
    >
      {disableSort ? (
        children
      ) : (
        <TableSortLabel
          active={sortBy === dataKey}
          direction={sortDirection ? sortDirection.toLowerCase() : undefined}
        >
          {children}
        </TableSortLabel>
      )}
      {resize && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={`Resize the ${resize.label || dataKey} column`}
          tabIndex={0}
          className={classnames(classes.resizeHandle, {
            [classes.resizing]: resizing,
          })}
          onMouseDown={startResize}
          onKeyDown={handleKeyDown}
          onClick={event => event.stopPropagation()}
        />
      )}
    </TableCell>
  );
};

ArchTableHeaderCell.propTypes = {
  children: PropTypes.node.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  style: PropTypes.object,
  dataKey: PropTypes.string,
  sortBy: PropTypes.string,
  sortDirection: PropTypes.string,
  disableSort: PropTypes.bool,
  // Present only for a column the user is allowed to resize.
  columnData: PropTypes.shape({
    rightAlign: PropTypes.bool,
    resize: PropTypes.shape({
      label: PropTypes.string,
      width: PropTypes.number.isRequired,
      minWidth: PropTypes.number.isRequired,
      maxWidth: PropTypes.number.isRequired,
      onResize: PropTypes.func.isRequired,
    }),
  }),
};

ArchTableHeaderCell.defaultProps = {
  style: null,
  dataKey: '',
  sortBy: '',
  sortDirection: '',
  disableSort: false,
  columnData: null,
};

export default withStyles(ArchTableHeaderCell, styles);
